import classNames from "classnames";
import { FormikProps } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DeliveryArea,
  DELIVERY_METHODS,
  StoreInterface,
  CouponItemInterface,
  CartItem as CartItemType,
} from "../../../assets/interfaces";
import { getFieldvalues, toCurrency } from "../../../assets/js/utils/functions";
import { AppBtn } from "../../ui/buttons";
import DataAccordion from "../../ui/data-accordion";
import ErrorLabel from "../../ui/error-label";
import { InputField, PhoneInput, SelectDropdown, TextArea } from "../../ui/form-elements";
import SelectDropdownWithModal from "../../ui/form-elements/select-dropdown-with-modal";
import { ModalBody, ModalFooter } from "../../ui/modal";
import { CheckoutForm, DEFAULT_ADDRESS_CACHE } from "../modals/checkout";
import CartContext, { FormatCurrencyFun } from "../../../contexts/cart-context";
import InputFieldWithAutocomplete, { InputOptions } from "@/components/ui/form-elements/input-field-with-autocomplete";
import AddressAutocompleteInput from "./address-autocomplete-input";
import Portal from "@/components/portal";
import { ApplyCouponModal } from "./apply-coupon-modal";
import { ModalsState, useModals } from "@/components/hooks/useModals";
import { toast } from "@/components/ui/toast";
import { computePaymentCouponDiscount, getItemThumbnail } from "@/assets/js/utils/utils";
import PreviousAddressModal from "./previous-address-modal";
import { useLocalObject } from "@/components/hooks/useLocalState";
import { CardWithDetails } from "@/components/ui/card-with-details";
import { GroupedCart } from "./cart";
import CartItem from "./cart-item";
import CartMainItem from "./cart-main-item";
import CollapsibleSummary from "./collapsible-summary";
import CartContextV2 from "@/contexts/cart-context-v2";

type ModalState = { [string: string]: { show: boolean } };

interface Props {
  store: StoreInterface;
  prevStep: VoidFunction;
  nextStep: VoidFunction;
  changeStep: (val: string) => void;
  scrollToTop?: boolean;
  form: FormikProps<CheckoutForm>;
  createOrderReq: any;
  formatAsCurrency: FormatCurrencyFun;
  couponModal: { modals: ModalsState<"coupon">; toggleModal: (key: string) => void };
  applyCoupon: (paymentCoupon: CouponItemInterface) => void;
  coupon: { couponCode: string; discountAmount: number };
  removeCoupon: VoidFunction;
}

const OrderSummary: React.FC<Props> = ({
  store,
  prevStep,
  scrollToTop,
  form,
  createOrderReq,
  formatAsCurrency,
  nextStep,
  changeStep,
  applyCoupon,
  removeCoupon,
  coupon,
  couponModal,
}) => {
  const { priceUnformatted, currencies, cart, setQuantity, addOrRemoveItem, updateItemQuantity, removeItem, price } =
    CartContextV2.useContainer();
  const [errorText, setErrorText] = useState("");
  const { modals, toggleModal } = useModals(["address"]);
  let modalBodyRef = useRef<HTMLDivElement>(null);

  const deliveryAreas = store.delivery_areas as DeliveryArea[];
  const { deliveringToSelf, deliveryMethod } = form.values;
  const hasCustomCheckoutForm =
    store?.configuration &&
    store?.configuration?.custom_checkout_form &&
    store?.configuration?.custom_checkout_form?.length > 0;

  useEffect(() => {
    form.setFieldValue("coupon", coupon?.couponCode ?? null);
  }, [coupon]);

  const customWithPrices =
    hasCustomCheckoutForm &&
    store?.configuration?.custom_checkout_form.filter((item) =>
      item?.options.some(
        (option) =>
          typeof option === "object" &&
          option !== null &&
          "value" in option &&
          "price" in option &&
          Number(option.price) > 0
      )
    );

  const orderAmount = useMemo(() => {
    let total = priceUnformatted;

    if (store.deliveries_enabled && form.values.deliveryMethod === DELIVERY_METHODS.DELIVERY && form.values.area) {
      const deliveryFee = deliveryAreas.find((area) => area.id === form.values.area)?.fee || 0;
      total += deliveryFee;
    }

    if (coupon) {
      total -= Math.abs(coupon.discountAmount);
    }

    if (store?.configuration.custom_checkout_form?.length > 0 && customWithPrices?.length > 0) {
      customWithPrices.forEach((item) => {
        const customItem = customWithPrices.find((i) => i.name === item.name);
        const optionPrice = Number(
          customItem?.options?.find((i) => i.value === form.values.extra_details[item.name])?.price || 0
        );
        total += optionPrice;
      });
    }

    return formatAsCurrency(total);
  }, [
    price,
    store.deliveries_enabled,
    form.values.deliveryMethod,
    form.values.area,
    store.delivery_areas,
    coupon,
    formatAsCurrency,
  ]);

  //   useEffect(() => {
  //     if (couponDataCache.current && priceUnformatted > 0) {
  //       applyCoupon(couponDataCache.current);
  //     }
  //   }, [priceUnformatted, couponDataCache]);

  useEffect(() => {
    scrollToTop && modalBodyRef.current.scrollTo(0, 0);
  }, [scrollToTop]);

  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (deliveringToSelf === undefined) {
      setErrorText("Please provide your delivery information");
      return;
    }

    form.handleSubmit(e);
  };

  const getItemPrice = (item: CartItemType, quantity: number) =>
    formatAsCurrency(
      (item.variant ? item.variant.discount_price ?? item.variant.price : item.item.discount_price ?? item.item.price) *
        quantity
    );

  const handlePreviousAddress = (address: string) => {
    form.setFieldValue("address", address);
    toggleModal("address");
  };

  const groupedCart = cart.reduce((acc: GroupedCart, item: CartItemType) => {
    const itemId = item.item.id;

    if (!acc[itemId]) {
      acc[itemId] = [];
    }

    acc[itemId].push(item);
    return acc;
  }, {});

  const groupedCartArray = Object.values(groupedCart);
  useEffect(() => {
    if (cart.length === 0) {
      changeStep("cart");
    }
  }, [cart.length]);
  return (
    <>
      <form className="flex flex-col flex-auto overflow-hidden" onSubmit={submitForm}>
        <ModalBody setRef={(el) => (modalBodyRef.current = el)} className="relative">
          <p className="text-black-muted text-sm md:text-1sm mb-3.75">
            You&quot;re almost there! Kindly fill in the following information before proceeding to checkout
          </p>
          <ErrorLabel error={createOrderReq?.error?.message ?? errorText} setError={setErrorText} />
          {coupon?.couponCode && false && (
            <div className="bg-white sticky top-0 z-[1000] -mx-5 sm:-mx-6.25 transform -translate-y-5 sm:-translate-y-6.25 shadow-card !mb-0">
              <div className="py-2.5 text-black-secondary bg-accent-yellow-500 bg-opacity-20 px-4 text-1xs sm:text-sm flex items-start sm:items-center justify-center">
                {/* prettier-ignore */}
                <svg width="20px" viewBox="0 0 24 24" fill="none" className="transition-all ease-out duration-200 text-success mr-2.5 flex-shrink-0">
                  <path d="M21.5289 10.8689L20.0089 9.34891C19.7489 9.08891 19.5389 8.57891 19.5389 8.21891V6.05891C19.5389 5.17891 18.8189 4.45891 17.9389 4.45891H15.7889C15.4289 4.45891 14.9189 4.24891 14.6589 3.98891L13.1389 2.46891C12.5189 1.84891 11.4989 1.84891 10.8789 2.46891L9.33891 3.98891C9.08891 4.24891 8.57891 4.45891 8.20891 4.45891H6.05891C5.17891 4.45891 4.45891 5.17891 4.45891 6.05891V8.20891C4.45891 8.56891 4.24891 9.07891 3.98891 9.33891L2.46891 10.8589C1.84891 11.4789 1.84891 12.4989 2.46891 13.1189L3.98891 14.6389C4.24891 14.8989 4.45891 15.4089 4.45891 15.7689V17.9189C4.45891 18.7989 5.17891 19.5189 6.05891 19.5189H8.20891C8.56891 19.5189 9.07891 19.7289 9.33891 19.9889L10.8589 21.5089C11.4789 22.1289 12.4989 22.1289 13.1189 21.5089L14.6389 19.9889C14.8989 19.7289 15.4089 19.5189 15.7689 19.5189H17.9189C18.7989 19.5189 19.5189 18.7989 19.5189 17.9189V15.7689C19.5189 15.4089 19.7289 14.8989 19.9889 14.6389L21.5089 13.1189C22.1589 12.5089 22.1589 11.4889 21.5289 10.8689ZM7.99891 8.99891C7.99891 8.44891 8.44891 7.99891 8.99891 7.99891C9.54891 7.99891 9.99891 8.44891 9.99891 8.99891C9.99891 9.54891 9.55891 9.99891 8.99891 9.99891C8.44891 9.99891 7.99891 9.54891 7.99891 8.99891ZM9.52891 15.5289C9.37891 15.6789 9.18891 15.7489 8.99891 15.7489C8.80891 15.7489 8.61891 15.6789 8.46891 15.5289C8.17891 15.2389 8.17891 14.7589 8.46891 14.4689L14.4689 8.46891C14.7589 8.17891 15.2389 8.17891 15.5289 8.46891C15.8189 8.75891 15.8189 9.23891 15.5289 9.52891L9.52891 15.5289ZM14.9989 15.9989C14.4389 15.9989 13.9889 15.5489 13.9889 14.9989C13.9889 14.4489 14.4389 13.9989 14.9889 13.9989C15.5389 13.9989 15.9889 14.4489 15.9889 14.9989C15.9889 15.5489 15.5489 15.9989 14.9989 15.9989Z" fill="currentColor"/>
                </svg>
                <div className="flex items-center flex-wrap">
                  <div className="mr-3">
                    <b className="font-semibold">{coupon?.couponCode} Applied</b>
                    &nbsp;- {formatAsCurrency(Math.abs(coupon?.discountAmount))} OFF
                  </div>
                  <button
                    className="bg-white rounded-5 shadow-card text-xs text-accent-red-500 px-2 py-1 font-medium"
                    onClick={removeCoupon}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )}

          {cart.length > 1 && (
            <CollapsibleSummary
              title={cart[0]?.item?.name}
              price={price}
              image={getItemThumbnail(cart[0]?.item)}
              itemsLength={cart.length}
            >
              {groupedCartArray.map((items, index) =>
                items.length > 0 && items[0].variant ? (
                  <CartMainItem items={items} key={index}>
                    {items.map((item, itemIndex) => (
                      <CartItem
                        key={itemIndex}
                        {...{
                          item,
                          updateItemQuantity,
                          addOrRemoveItem,
                          setItemQuantity: setQuantity,
                          removeItem,
                          formatAsCurrency,
                        }}
                      />
                    ))}
                  </CartMainItem>
                ) : (
                  <CartItem
                    key={index}
                    {...{
                      item: items[0],
                      updateItemQuantity,
                      addOrRemoveItem,
                      setItemQuantity: setQuantity,
                      removeItem,
                      formatAsCurrency,
                    }}
                  />
                )
              )}
            </CollapsibleSummary>
          )}
          {cart.length === 1 && (
            <>
              {groupedCartArray.map((items, index) =>
                items.length > 0 && items[0].variant ? (
                  <CartMainItem items={items} key={index}>
                    {items.map((item, itemIndex) => (
                      <CartItem
                        key={itemIndex}
                        {...{
                          item,
                          updateItemQuantity,
                          addOrRemoveItem,
                          setItemQuantity: setQuantity,
                          removeItem,
                          formatAsCurrency,
                        }}
                      />
                    ))}
                  </CartMainItem>
                ) : (
                  <CartItem
                    key={index}
                    {...{
                      item: items[0],
                      updateItemQuantity,
                      addOrRemoveItem,
                      setItemQuantity: setQuantity,
                      removeItem,
                      formatAsCurrency,
                    }}
                  />
                )
              )}
            </>
          )}
          <div>
            <InfoCard
              title="Purchase Information"
              icon={
                <>
                  {" "}
                  {/* prettier-ignore */}
                  <svg width={16} height={17} viewBox="0 0 16 17" fill="currentColor">
                    <path d="M13.2199 6.29954L10.1999 3.27954C9.56661 2.64621 8.69328 2.30621 7.79994 2.35288L4.46661 2.51288C3.13328 2.57288 2.07328 3.63288 2.00661 4.95954L1.84661 8.29288C1.80661 9.18621 2.13994 10.0595 2.77328 10.6929L5.79328 13.7129C7.03328 14.9529 9.04661 14.9529 10.2933 13.7129L13.2199 10.7862C14.4666 9.55288 14.4666 7.53954 13.2199 6.29954ZM6.33328 8.75288C5.27994 8.75288 4.41328 7.89288 4.41328 6.83288C4.41328 5.77288 5.27994 4.91288 6.33328 4.91288C7.38661 4.91288 8.25328 5.77288 8.25328 6.83288C8.25328 7.89288 7.38661 8.75288 6.33328 8.75288ZM11.6866 9.51954L9.01994 12.1862C8.91994 12.2862 8.79328 12.3329 8.66661 12.3329C8.53994 12.3329 8.41328 12.2862 8.31328 12.1862C8.11994 11.9929 8.11994 11.6729 8.31328 11.4795L10.9799 8.81288C11.1733 8.61954 11.4933 8.61954 11.6866 8.81288C11.8799 9.00621 11.8799 9.32621 11.6866 9.51954Z" fill="#AAAAAA" />
                  </svg>
                </>
              }
            >
              {form.values.deliveringToSelf && (
                <div className="flex flex-col gap-y-1 justify-between items-start">
                  <span className="text-sm sm:text-sm text-black-muted">{form.values.name}</span>
                  <span className="text-sm sm:text-sm text-black-muted">
                    {form.values.phone.code}
                    {form.values.phone.digits}
                  </span>
                  <span className="text-sm sm:text-sm text-black-muted">{form.values.email}</span>
                </div>
              )}

              {!form.values.deliveringToSelf && (
                <div className="flex flex-col gap-y-1 justify-between items-start">
                  <span className="text-sm sm:text-sm text-black-muted">{form.values.deliveryContact.name}</span>
                  <span className="text-sm sm:text-sm text-black-muted">
                    {form.values.deliveryContact.phone.code}
                    {form.values.deliveryContact.phone.digits}
                  </span>
                </div>
              )}
            </InfoCard>
            {deliveryMethod == DELIVERY_METHODS.PICKUP && (
              <InfoCard
                title="Pickup Location"
                icon={
                  <>
                    {" "}
                    {/* prettier-ignore */}
                    <svg width={16} height={17} viewBox="0 0 16 17" fill="none" >
                  <path d="M13.4734 5.71329L8.34002 8.68663C8.13335 8.80663 7.87335 8.80663 7.66002 8.68663L2.52669 5.71329C2.16002 5.49996 2.06669 4.99996 2.34669 4.68663C2.54002 4.46663 2.76002 4.28663 2.99335 4.15996L6.60669 2.15996C7.38002 1.72663 8.63335 1.72663 9.40669 2.15996L13.02 4.15996C13.2534 4.28663 13.4734 4.47329 13.6667 4.68663C13.9334 4.99996 13.84 5.49996 13.4734 5.71329Z" fill="#AAAAAA" />
                  <path d="M7.62003 9.92626V14.4729C7.62003 14.9796 7.10669 15.3129 6.65336 15.0929C5.28003 14.4196 2.96669 13.1596 2.96669 13.1596C2.15336 12.6996 1.48669 11.5396 1.48669 10.5863V7.14626C1.48669 6.61959 2.04003 6.28626 2.49336 6.54626L7.28669 9.32626C7.48669 9.45293 7.62003 9.67959 7.62003 9.92626Z" fill="#AAAAAA" />
                  <path d="M8.38 9.92626V14.4729C8.38 14.9796 8.89334 15.3129 9.34667 15.0929C10.72 14.4196 13.0333 13.1596 13.0333 13.1596C13.8467 12.6996 14.5133 11.5396 14.5133 10.5863V7.14626C14.5133 6.61959 13.96 6.28626 13.5067 6.54626L8.71334 9.32626C8.51334 9.45293 8.38 9.67959 8.38 9.92626Z" fill="#AAAAAA" />
                </svg>
                  </>
                }
              >
                <div className="flex justify-between items-start">
                  <span className="text-sm sm:text-sm text-black-muted">
                    {store?.pickup_address?.formatted_address}
                  </span>
                </div>
              </InfoCard>
            )}
            {deliveryMethod == DELIVERY_METHODS.DELIVERY && (
              <InfoCard
                title="Delivery Location"
                icon={
                  <>
                    {" "}
                    {/* prettier-ignore */}
                    <svg width={16} height={17} viewBox="0 0 16 17" fill="none" >
                  <path d="M13.4734 5.71329L8.34002 8.68663C8.13335 8.80663 7.87335 8.80663 7.66002 8.68663L2.52669 5.71329C2.16002 5.49996 2.06669 4.99996 2.34669 4.68663C2.54002 4.46663 2.76002 4.28663 2.99335 4.15996L6.60669 2.15996C7.38002 1.72663 8.63335 1.72663 9.40669 2.15996L13.02 4.15996C13.2534 4.28663 13.4734 4.47329 13.6667 4.68663C13.9334 4.99996 13.84 5.49996 13.4734 5.71329Z" fill="#AAAAAA" />
                  <path d="M7.62003 9.92626V14.4729C7.62003 14.9796 7.10669 15.3129 6.65336 15.0929C5.28003 14.4196 2.96669 13.1596 2.96669 13.1596C2.15336 12.6996 1.48669 11.5396 1.48669 10.5863V7.14626C1.48669 6.61959 2.04003 6.28626 2.49336 6.54626L7.28669 9.32626C7.48669 9.45293 7.62003 9.67959 7.62003 9.92626Z" fill="#AAAAAA" />
                  <path d="M8.38 9.92626V14.4729C8.38 14.9796 8.89334 15.3129 9.34667 15.0929C10.72 14.4196 13.0333 13.1596 13.0333 13.1596C13.8467 12.6996 14.5133 11.5396 14.5133 10.5863V7.14626C14.5133 6.61959 13.96 6.28626 13.5067 6.54626L8.71334 9.32626C8.51334 9.45293 8.38 9.67959 8.38 9.92626Z" fill="#AAAAAA" />
                </svg>
                  </>
                }
              >
                <div className="flex justify-between items-start">
                  <span className="text-sm sm:text-sm text-black-muted overflow-ellipsis">{form.values.address}</span>
                </div>
              </InfoCard>
            )}

            {deliveryMethod == DELIVERY_METHODS.DELIVERY && (
              <InfoCard
                title="Delivery Area"
                icon={
                  <>
                    {" "}
                    {/* prettier-ignore */}
                    <svg width={16} height={17} viewBox="0 0 16 17" fill="none" >
                  <path d="M13.4734 5.71329L8.34002 8.68663C8.13335 8.80663 7.87335 8.80663 7.66002 8.68663L2.52669 5.71329C2.16002 5.49996 2.06669 4.99996 2.34669 4.68663C2.54002 4.46663 2.76002 4.28663 2.99335 4.15996L6.60669 2.15996C7.38002 1.72663 8.63335 1.72663 9.40669 2.15996L13.02 4.15996C13.2534 4.28663 13.4734 4.47329 13.6667 4.68663C13.9334 4.99996 13.84 5.49996 13.4734 5.71329Z" fill="#AAAAAA" />
                  <path d="M7.62003 9.92626V14.4729C7.62003 14.9796 7.10669 15.3129 6.65336 15.0929C5.28003 14.4196 2.96669 13.1596 2.96669 13.1596C2.15336 12.6996 1.48669 11.5396 1.48669 10.5863V7.14626C1.48669 6.61959 2.04003 6.28626 2.49336 6.54626L7.28669 9.32626C7.48669 9.45293 7.62003 9.67959 7.62003 9.92626Z" fill="#AAAAAA" />
                  <path d="M8.38 9.92626V14.4729C8.38 14.9796 8.89334 15.3129 9.34667 15.0929C10.72 14.4196 13.0333 13.1596 13.0333 13.1596C13.8467 12.6996 14.5133 11.5396 14.5133 10.5863V7.14626C14.5133 6.61959 13.96 6.28626 13.5067 6.54626L8.71334 9.32626C8.51334 9.45293 8.38 9.67959 8.38 9.92626Z" fill="#AAAAAA" />
                </svg>
                  </>
                }
              >
                <div className="flex justify-between items-start">
                  <span className="text-sm sm:text-sm text-black-muted">
                    {deliveryAreas.find((area) => area.id === form.values.area)?.name}
                    <br />
                    <b className="mt-1 block">
                      {formatAsCurrency(deliveryAreas.find((area) => area.id === form.values.area)?.fee)}
                    </b>
                  </span>
                </div>
              </InfoCard>
            )}

            {form.values.orderNotes && (
              <InfoCard
                title="Extra Information"
                icon={
                  <>
                    {" "}
                    {/* prettier-ignore */}
                    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.7934 1.83301H5.20671C2.78004 1.83301 1.33337 3.27967 1.33337 5.70634V11.293C1.33337 13.7197 2.78004 15.1663 5.20671 15.1663H10.7934C13.22 15.1663 14.6667 13.7197 14.6667 11.293V5.70634C14.6667 3.27967 13.22 1.83301 10.7934 1.83301ZM6.64671 10.433L5.14671 11.933C5.04671 12.033 4.92004 12.0797 4.79337 12.0797C4.66671 12.0797 4.53337 12.033 4.44004 11.933L3.94004 11.433C3.74004 11.2397 3.74004 10.9197 3.94004 10.7263C4.13337 10.533 4.44671 10.533 4.64671 10.7263L4.79337 10.873L5.94004 9.72634C6.13337 9.53301 6.44671 9.53301 6.64671 9.72634C6.84004 9.91967 6.84004 10.2397 6.64671 10.433ZM6.64671 5.76634L5.14671 7.26634C5.04671 7.36634 4.92004 7.41301 4.79337 7.41301C4.66671 7.41301 4.53337 7.36634 4.44004 7.26634L3.94004 6.76634C3.74004 6.57301 3.74004 6.25301 3.94004 6.05967C4.13337 5.86634 4.44671 5.86634 4.64671 6.05967L4.79337 6.20634L5.94004 5.05967C6.13337 4.86634 6.44671 4.86634 6.64671 5.05967C6.84004 5.25301 6.84004 5.57301 6.64671 5.76634ZM11.7067 11.5797H8.20671C7.93337 11.5797 7.70671 11.353 7.70671 11.0797C7.70671 10.8063 7.93337 10.5797 8.20671 10.5797H11.7067C11.9867 10.5797 12.2067 10.8063 12.2067 11.0797C12.2067 11.353 11.9867 11.5797 11.7067 11.5797ZM11.7067 6.91301H8.20671C7.93337 6.91301 7.70671 6.68634 7.70671 6.41301C7.70671 6.13967 7.93337 5.91301 8.20671 5.91301H11.7067C11.9867 5.91301 12.2067 6.13967 12.2067 6.41301C12.2067 6.68634 11.9867 6.91301 11.7067 6.91301Z" fill="#AAAAAA"/>
                  </svg>
                  </>
                }
              >
                <div className="flex justify-between items-start">
                  <span className="text-sm sm:text-sm text-black-muted">{form.values.orderNotes}</span>
                </div>
              </InfoCard>
            )}
            {cart.length > 0 && !coupon && (
              <button
                type="button"
                onClick={() => couponModal.toggleModal("coupon")}
                className="text-black-secondary flex text-1xs sm:text-sm font-semibold underline my-3.75"
              >
                Enter a Coupon Code
              </button>
            )}
            {coupon?.couponCode && (
              <div className="bg-white  my-3.75 z-[1000]">
                <div className="py-3.75 text-black-muted rounded-10 bg-grey-fields-100 px-4 text-1xs sm:text-sm flex items-start sm:items-center justify-center">
                  {/* prettier-ignore */}
                  <svg  width="20px" viewBox="0 0 23 22" fill="none" className="transition-all ease-out duration-200 text-primary-500 mr-1 flex-shrink-0">
                <path d="M20.2358 9.96414L18.8425 8.57081C18.6041 8.33247 18.4116 7.86497 18.4116 7.53497V5.55497C18.4116 4.74831 17.7516 4.08831 16.945 4.08831H14.9741C14.6441 4.08831 14.1766 3.89581 13.9383 3.65747L12.545 2.26414C11.9766 1.69581 11.0416 1.69581 10.4733 2.26414L9.06164 3.65747C8.83247 3.89581 8.36497 4.08831 8.02581 4.08831H6.05497C5.24831 4.08831 4.58831 4.74831 4.58831 5.55497V7.52581C4.58831 7.85581 4.39581 8.32331 4.15747 8.56164L2.76414 9.95497C2.19581 10.5233 2.19581 11.4583 2.76414 12.0266L4.15747 13.42C4.39581 13.6583 4.58831 14.1258 4.58831 14.4558V16.4266C4.58831 17.2333 5.24831 17.8933 6.05497 17.8933H8.02581C8.35581 17.8933 8.82331 18.0858 9.06164 18.3241L10.455 19.7175C11.0233 20.2858 11.9583 20.2858 12.5266 19.7175L13.92 18.3241C14.1583 18.0858 14.6258 17.8933 14.9558 17.8933H16.9266C17.7333 17.8933 18.3933 17.2333 18.3933 16.4266V14.4558C18.3933 14.1258 18.5858 13.6583 18.8241 13.42L20.2175 12.0266C20.8133 11.4675 20.8133 10.5325 20.2358 9.96414ZM7.83331 8.24997C7.83331 7.74581 8.24581 7.33331 8.74997 7.33331C9.25414 7.33331 9.66664 7.74581 9.66664 8.24997C9.66664 8.75414 9.26331 9.16664 8.74997 9.16664C8.24581 9.16664 7.83331 8.75414 7.83331 8.24997ZM9.23581 14.2358C9.09831 14.3733 8.92414 14.4375 8.74997 14.4375C8.57581 14.4375 8.40164 14.3733 8.26414 14.2358C7.99831 13.97 7.99831 13.53 8.26414 13.2641L13.7641 7.76414C14.03 7.49831 14.47 7.49831 14.7358 7.76414C15.0016 8.02997 15.0016 8.46997 14.7358 8.73581L9.23581 14.2358ZM14.25 14.6666C13.7366 14.6666 13.3241 14.2541 13.3241 13.75C13.3241 13.2458 13.7366 12.8333 14.2408 12.8333C14.745 12.8333 15.1575 13.2458 15.1575 13.75C15.1575 14.2541 14.7541 14.6666 14.25 14.6666Z" fill="currentColor"/>
                </svg>
                  <div className="flex items-center justify-between w-full">
                    <div className="mr-3">
                      <b className="font-semibold">{coupon?.couponCode} Applied</b>
                    </div>
                    <button
                      className="rounded-5 shadow-card text-xs text-accent-red-500 px-0 py-0 font-medium"
                      onClick={removeCoupon}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            )}
            <span className="mb-3.75 font-bold text-black-muted text-1sm">SUMMARY</span>
            <div className="space-y-3 mt-3.75">
              <div className="flex items-center justify-between text-sm mb-2.5">
                <span className="text-dark">Total Items ({cart.length})</span>
                <span className="text-black-secondary font-medium">{price}</span>
              </div>
              {store.deliveries_enabled && deliveryMethod == DELIVERY_METHODS.DELIVERY && form.values.area && (
                <div className="flex items-center justify-between text-sm mb-2.5">
                  <span className="text-dark">Delivery Fee</span>
                  <span className="text-black-secondary font-medium">
                    {formatAsCurrency(deliveryAreas.find((i) => i.id === form.values.area).fee)}
                  </span>
                </div>
              )}
              {coupon && (
                <div className="flex items-center justify-between text-sm mb-2.5">
                  <span className="text-dark">Coupon</span>
                  <span className="text-black-secondary font-medium">
                    - {formatAsCurrency(Math.abs(coupon?.discountAmount))}
                  </span>
                </div>
              )}
              {store.configuration.custom_checkout_form?.length > 0 &&
                customWithPrices.length > 0 &&
                customWithPrices.map((item, id) => {
                  const foundEntry = Object.entries(form.values.extra_details).find(([key]) => key === item.name);
                  const customItem = customWithPrices.find((i) => i.name === item.name);
                  const optionPrice = customItem?.options?.find(
                    (i) => i.value === form.values.extra_details[item.name]
                  )?.price;
                  return (
                    <div key={id} className="flex items-center justify-between text-sm mb-2.5">
                      <span className="text-dark">{foundEntry ? foundEntry[1] : "N/A"}</span>
                      <span className="text-black-secondary font-medium">{formatAsCurrency(optionPrice)}</span>
                    </div>
                  );
                })}
              <div className="flex items-center justify-between text-sm mb-2.5">
                <span className="text-black font-medium">Total</span>
                <span className="text-black-secondary font-bold">{orderAmount}</span>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex gap-2.5 w-full">
            <AppBtn onClick={prevStep} className="flex-1" color="neutral" size="lg">
              Go Back
            </AppBtn>
            <AppBtn className="flex-1" isBlock type="submit" disabled={createOrderReq.isLoading} size="lg">
              {createOrderReq.isLoading ? "Placing Order..." : "Complete Order"}
            </AppBtn>
          </div>
        </ModalFooter>
      </form>
      <Portal>
        <ApplyCouponModal
          toggle={() => couponModal.toggleModal("coupon")}
          applyCoupon={applyCoupon}
          store={store.id}
          show={couponModal.modals.coupon}
          itemsAmount={priceUnformatted}
          currency={currencies.active}
          customerId={form?.values?.customer_id}
        />
        <PreviousAddressModal
          toggle={() => toggleModal("address")}
          onSelect={handlePreviousAddress}
          show={modals.address}
          address={form.values.address}
          deliveriesEnabled={store?.deliveries_enabled}
        />
      </Portal>
    </>
  );
};

type InfoProps = {
  title: string;
  icon: React.ReactNode;
};

const InfoCard: React.FC<InfoProps> = ({ children, title, icon }) => {
  return (
    <div className="flex mt-3.75 flex-col rounded-10 p-4.5 sm:p-5 bg-grey-fields-200 w-full">
      <div className="flex justify-between">
        <div className="flex gap-1.5 items-center">
          {icon}

          <span className="text-sm sm:text-base font-bold font-display text-black">{title} </span>
        </div>
        <div className="flex-shrink-0">
          <figure className="rounded-full w-5 h-5 sm:h-7 sm:w-7 text-success bg-white flex items-center justify-center">
            {/* prettier-ignore */}
            <svg width={"50%"} viewBox="0 0 16 16" fill="none" >
              <path d="M5.31307 2.97945C5.13556 3.15697 5.11942 3.43474 5.26466 3.63048L5.31307 3.68656L9.62596 7.99968L5.31307 12.3128C5.13556 12.4903 5.11942 12.7681 5.26466 12.9638L5.31307 13.0199C5.49058 13.1974 5.76836 13.2135 5.9641 13.0683L6.02018 13.0199L10.6868 8.35323C10.8644 8.17572 10.8805 7.89794 10.7353 7.7022L10.6868 7.64612L6.02018 2.97945C5.82492 2.78419 5.50833 2.78419 5.31307 2.97945Z" fill="currentColor" />
            </svg>
          </figure>
        </div>
      </div>
      <div className="w-80 mt-1">{children}</div>
    </div>
  );
};
export default OrderSummary;
